import * as React from 'react'

import { action, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { NotificationType, SitePermitStatus } from '~/client/graph'
import MobileEventStore from '~/client/src/mobile/stores/EventStore/MobileEvents.store'
import MobileInitialState from '~/client/src/mobile/stores/MobileInitialState'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import DeliveryStatusIcon from '~/client/src/shared/components/DeliveryIconByStatus'
import DeliveryStatusLabel from '~/client/src/shared/components/DeliveryStatusLabel/DeliveryStatusLabel'
import { ArchiveIcon } from '~/client/src/shared/components/icons/ArchiveIcon'
import { CheckCircleFillIcon } from '~/client/src/shared/components/icons/CheckCircleFillIcon'
import { ImportantAtIcon } from '~/client/src/shared/components/icons/ImportantAtIcon'
import { ImportantBicIcon } from '~/client/src/shared/components/icons/ImportantBicIcon'
import { StateUnfilledCheckboxRadioIcon } from '~/client/src/shared/components/icons/StateUnfilledCheckboxRadioIcon'
import { UserIcon } from '~/client/src/shared/components/icons/UserIcon'
import PermitListItem from '~/client/src/shared/components/PermitListItem'
import PermitTypeIcon from '~/client/src/shared/components/PermitTypeIcon/PermitTypeIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import { NotificationsListStore } from '~/client/src/shared/lists/notifications/NotificationsList.store'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import BaseNotification from '~/client/src/shared/models/Notification'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MessagesStore from '~/client/src/shared/stores/domain/MessagesStore/Messages.store'
import OffloadingEquipmentsStore from '~/client/src/shared/stores/domain/OffloadingEquipments.store'
import PermitTypesStore from '~/client/src/shared/stores/domain/PermitTypes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import ZonesStore from '~/client/src/shared/stores/domain/Zones.store'
import {
  MARK_NOTIFICATIONS_AS_ARCHIVED,
  SAVE_SITE_PERMITS,
  VIEW_NOTIFICATION,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { isDeleteType } from '~/client/src/shared/types/NotificationTypes'
import canApproveUser from '~/client/src/shared/utils/canApproveUser'
import ElementSwiper from '~/client/src/shared/utils/elementSwipeHelper'
import {
  EntityData,
  getAuthorAndAction,
  getCodeAndTitle,
  getCompanyName,
  getDateDisplay,
  getEntityTypeName,
  getEquipmentText,
  getImportantKind,
  getLocationTagData,
  getUserSignupMeta,
  resolveEntityData,
} from '~/client/src/shared/utils/notificationItemHelpers'
import {
  currentStepHasMandatoryFields,
  getForwardActionLabel,
  getForwardActionTargetStatus,
} from '~/client/src/shared/utils/permitStepActionHelpers'
import { toast } from '~/client/src/shared/utils/toast'
import { INTERPUNCT } from '~/client/src/shared/utils/usefulStrings'
import { advanceToNextWorkflowStep } from '~/client/src/shared/utils/workflowStepUtils'

const ARCHIVE_TOAST_TIMEOUT = 3000
let archiveCount = 0
let archiveResetTimer: ReturnType<typeof setTimeout> | null = null

interface IProps {
  recordId: string
  notification: BaseNotification
  isMultiSelectMode?: boolean
  notificationsListStore: NotificationsListStore
  interactionHandlers?: IInteractionHandlers
  eventsStore?: MobileEventStore
  deliveriesStore?: DeliveriesStore
  companiesStore?: CompaniesStore
  permitTypesStore?: PermitTypesStore
  projectMembersStore?: ProjectMembersStore
  projectDateStore?: ProjectDateStore
  zonesStore?: ZonesStore
  offloadingEquipmentsStore?: OffloadingEquipmentsStore
  locationAttributesStore?: LocationAttributesStore
  messagesStore?: MessagesStore
  userProjectsStore?: UserProjectsStore
  permitBicStore?: PermitBallInCourtStore
  state?: MobileInitialState
}

interface IInteractionHandlers {
  onClick(e: React.MouseEvent): void
  onPointerDown(e: React.PointerEvent): void
  onPointerMove(e: React.PointerEvent): void
  onPointerUp(e: React.PointerEvent): void
  onPointerCancel(e: React.PointerEvent): void
}

@inject(
  'eventsStore',
  'deliveriesStore',
  'companiesStore',
  'permitTypesStore',
  'projectMembersStore',
  'projectDateStore',
  'zonesStore',
  'offloadingEquipmentsStore',
  'locationAttributesStore',
  'messagesStore',
  'userProjectsStore',
  'permitBicStore',
  'state',
)
@observer
export default class NotificationItem extends React.Component<IProps> {
  private elementSwiper: ElementSwiper
  private wrapperRef: HTMLDivElement = null

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (!prevProps.isMultiSelectMode && this.props.isMultiSelectMode) {
      if (this.elementSwiper) {
        this.elementSwiper.disconnectElement()
        this.elementSwiper = null
      }
    }
  }

  public componentWillUnmount() {
    if (this.elementSwiper) {
      this.elementSwiper.disconnectElement()
    }
  }

  public render() {
    const { wasRead, isArchived } = this.props.notification
    const entityData = this.entityData
    const { isMultiSelectMode } = this.props
    const isSelected = this.isSelected
    const isPermit = entityData.kind === 'permit' && !!entityData.permit

    return (
      <div ref={this.setWrapperRef}>
        <div
          className="notification-item"
          ref={isMultiSelectMode ? undefined : this.setContainerRef}
        >
          {!isArchived && !isMultiSelectMode && (
            <div className="notification-item-swipe-area">
              <div className="tw:flex tw:items-center tw:justify-center tw:gap-1">
                <ArchiveIcon size="sm" />
                <div>{Localization.translator.archive}</div>
              </div>
            </div>
          )}
          <div
            onClick={this.props.interactionHandlers?.onClick}
            onPointerDown={this.props.interactionHandlers?.onPointerDown}
            onPointerMove={this.props.interactionHandlers?.onPointerMove}
            onPointerUp={this.props.interactionHandlers?.onPointerUp}
            onPointerCancel={this.props.interactionHandlers?.onPointerCancel}
            className={classList({
              'pt12 pb12 px10 full-width': true,
              'notification-item-content': true,
              'notification-read': wasRead,
              'notification-item-selected': isSelected,
            })}
          >
            {isPermit
              ? this.renderPermitContent(entityData)
              : this.renderDefaultContent(entityData)}
          </div>
        </div>
      </div>
    )
  }

  private renderPermitContent(entityData: EntityData): JSX.Element {
    const { notification } = this.props
    const { wasRead } = notification
    const { isMultiSelectMode } = this.props

    if (entityData.kind !== 'permit' || !entityData.permit) return null

    return (
      <div className="col">
        <PermitListItem
          permit={entityData.permit}
          hideBreadcrumbs
          className="tw:pb-2"
          isSelectionMode={isMultiSelectMode}
          isSelected={this.isSelected}
          importantKind={getImportantKind(
            notification,
            this.props.state?.user?.id,
            this.props.permitTypesStore,
            this.props.permitBicStore,
          )}
        />

        {/* Message + timestamp row */}
        <div className="row y-center pl25">
          <span className="text large ellipsis notification-secondary-text">
            {getAuthorAndAction(
              notification,
              entityData,
              this.props.projectMembersStore,
              this.props.messagesStore,
            )}
          </span>
          <span
            className={`ml-auto text no-grow no-white-space-wrap h24 row y-center ${
              wasRead
                ? 'notification-secondary-text'
                : 'bold notification-dark-text'
            }`}
          >
            {this.formattedTimestamp}
          </span>
        </div>

        {/* BIC action button */}
        {this.renderBicActionButton()}
        {this.renderApproveActionButton()}
      </div>
    )
  }

  private renderDefaultContent(entityData: EntityData): JSX.Element {
    const { wasRead } = this.props.notification
    const { isMultiSelectMode } = this.props

    return (
      <div className="col">
        {/* Row 1: Icon + Title + Status + ImportantIcon */}
        <div className="row y-center notification-card-row1">
          <span className="row x-center y-center no-grow no-shrink notification-card-icon">
            {isMultiSelectMode
              ? this.renderSelectionIndicator()
              : this.renderEntityIcon(entityData)}
          </span>
          <span
            className={`text ellipsis notification-card-title ${
              wasRead
                ? 'notification-secondary-text'
                : 'bold notification-dark-text'
            }`}
          >
            {getCodeAndTitle(
              entityData,
              this.props.notification,
              this.props.companiesStore,
            )}
          </span>
          <span className="row no-grow no-shrink notification-card-badges">
            {this.renderStatusBadge(entityData)}
            {this.renderImportantIcon()}
          </span>
        </div>

        {/* Row 2: Type · Company */}
        {this.renderTypeLine(entityData)}

        {/* Row 3: Date · Location */}
        {this.renderDateLine(entityData)}

        {/* Row 4: Author action + timestamp */}
        <div className="row y-end mt4 notification-card-indented">
          <span className="text large notification-secondary-text tw:break-words tw:min-w-0 tw:leading-5">
            {getAuthorAndAction(
              this.props.notification,
              entityData,
              this.props.projectMembersStore,
              this.props.messagesStore,
            )}
          </span>
          <span
            className={`ml-auto text no-grow no-white-space-wrap h24 row y-center ${
              wasRead
                ? 'notification-secondary-text'
                : 'bold notification-dark-text'
            }`}
          >
            {this.formattedTimestamp}
          </span>
        </div>

        {/* Row 5: BIC action button (conditional) */}
        {this.renderBicActionButton()}
        {this.renderApproveActionButton()}
      </div>
    )
  }

  // --------------- Entity resolution ---------------

  private get formattedTimestamp(): string {
    return this.props.projectDateStore.getSmartTimestamp(
      this.props.notification.createdAt,
    )
  }

  private get entityData(): EntityData {
    return resolveEntityData(this.props.notification, {
      deliveriesStore: this.props.deliveriesStore,
    })
  }

  // --------------- Render helpers ---------------

  private renderEntityIcon(data: EntityData): JSX.Element {
    const { notification, permitTypesStore } = this.props
    const { type } = notification

    if (isDeleteType(type)) return null

    if (type === NotificationType.NewUserCreatedViaSignUp) {
      return <UserIcon size="md" className="tw:text-foreground" />
    }

    switch (data.kind) {
      case 'permit':
        if (!data.permit) return null
        return (
          <PermitTypeIcon
            iconName={data.permit.getIconNameOfPermitType(permitTypesStore)}
          />
        )
      case 'delivery':
        if (!data.delivery) return null
        return <DeliveryStatusIcon status={data.delivery.status} />
      default:
        return null
    }
  }

  private renderStatusBadge(data: EntityData): JSX.Element {
    const { notification } = this.props

    if (notification.type === NotificationType.NewUserCreatedViaSignUp) {
      const user = this.props.projectMembersStore.getById(notification.entityId)
      const canApprove =
        user && canApproveUser(user, this.props.userProjectsStore)
      return (
        <StatusBadge
          status={
            canApprove ? SitePermitStatus.Requested : SitePermitStatus.Accepted
          }
          isLate={false}
          showDot={false}
        />
      )
    }

    switch (data.kind) {
      case 'delivery':
        if (!data.delivery) return null
        return <DeliveryStatusLabel status={data.delivery.status} />
      case 'permit':
        if (!data.permit) return null
        return (
          <StatusBadge
            status={data.permit.status}
            isLate={data.permit.isLate}
            showDot={false}
          />
        )
      default:
        return null
    }
  }

  private renderImportantIcon(): JSX.Element {
    const { notification, permitTypesStore, state, permitBicStore } = this.props
    const userId = state?.user?.id
    const kind = getImportantKind(
      notification,
      userId,
      permitTypesStore,
      permitBicStore,
    )

    switch (kind) {
      case 'bic':
        return (
          <span className="row x-center y-center no-grow no-shrink w20 h20">
            <ImportantBicIcon size="sm" />
          </span>
        )
      case 'mention':
        return (
          <span className="row x-center y-center no-grow no-shrink w20 h20">
            <ImportantAtIcon size="sm" />
          </span>
        )
      default:
        return null
    }
  }

  private renderTypeLine(entityData: EntityData): JSX.Element {
    const typeName = getEntityTypeName(entityData, this.props.permitTypesStore)
    let companyName = getCompanyName(entityData, this.props.companiesStore)

    // User access request: show company (with avatar if available) · user name
    if (
      this.props.notification.type === NotificationType.NewUserCreatedViaSignUp
    ) {
      const meta = getUserSignupMeta(
        this.props.notification,
        this.props.projectMembersStore,
        this.props.companiesStore,
        this.props.userProjectsStore,
      )
      if (!companyName && meta.companyName) companyName = meta.companyName
      if (!companyName && !meta.userName) return null

      const company = meta.companyId
        ? this.props.companiesStore.getCompanyById(meta.companyId)
        : null

      return (
        <div className="row y-center mt4 notification-card-indented">
          {company && companyName && (
            <Avatar
              src={company.avatarUrl}
              size={AvatarSize.XMinuscule}
              className="mr4"
            />
          )}
          <span className="text large ellipsis notification-secondary-text">
            {[companyName, meta.userName]
              .filter(Boolean)
              .join(` ${INTERPUNCT} `)}
          </span>
        </div>
      )
    }

    const parts = [typeName, companyName].filter(Boolean)
    if (!parts.length) return null

    return (
      <div className="row y-center mt4 notification-card-indented">
        <span className="text large ellipsis notification-secondary-text">
          {parts.join(` ${INTERPUNCT} `)}
        </span>
      </div>
    )
  }

  private renderDateLine(entityData: EntityData): JSX.Element {
    const dateDisplay = getDateDisplay(entityData, this.props.projectDateStore)
    const equipmentText = getEquipmentText(
      entityData,
      this.props.offloadingEquipmentsStore,
    )
    const tagData = getLocationTagData(
      entityData,
      this.props.locationAttributesStore,
      this.props.zonesStore,
    )

    if (!dateDisplay && !equipmentText && !tagData) return null

    return (
      <div className="row y-center mt4 notification-card-indented">
        {dateDisplay && (
          <span className="text large notification-secondary-text no-grow no-white-space-wrap">
            {dateDisplay}
          </span>
        )}
        {tagData && this.renderLocationTags(tagData)}
        {equipmentText && (
          <span className="text large notification-secondary-text no-grow no-white-space-wrap">
            {` ${INTERPUNCT} ${equipmentText}`}
          </span>
        )}
      </div>
    )
  }

  private renderLocationTags(
    tagData: NonNullable<ReturnType<typeof getLocationTagData>>,
  ): JSX.Element {
    if (tagData.kind === 'permit') {
      return (
        <>
          {tagData.locations.map(dto => (
            <React.Fragment key={dto.id}>
              <span className="no-grow notification-secondary-text">
                {INTERPUNCT}
              </span>
              <span
                className="no-grow no-white-space-wrap row y-center"
                style={{ color: dto.color }}
              >
                <SitemapAttributeTag
                  contentContainerClassName="text-ellipsis"
                  dataObject={dto}
                  shouldShowAsTag={false}
                >
                  <span className="text large notification-secondary-text">
                    {dto.name}
                  </span>
                </SitemapAttributeTag>
              </span>
            </React.Fragment>
          ))}
        </>
      )
    }

    return (
      <span className="text large notification-secondary-text no-grow no-white-space-wrap">
        {` ${INTERPUNCT} ${tagData.zoneName}`}
      </span>
    )
  }

  private renderBicActionButton(): JSX.Element {
    const { notification, permitTypesStore, state, permitBicStore } = this.props
    const userId = state?.user?.id
    const kind = getImportantKind(
      notification,
      userId,
      permitTypesStore,
      permitBicStore,
    )
    const isAdmin = state?.userActiveProjectSettings?.isAdmin
    if (kind !== 'bic' && !isAdmin) return null

    const data = this.entityData
    if (data.kind !== 'permit' || !data.permit) return null

    const permit = data.permit
    if (permit.isDoneOrDenied || permit.isDeleted) return null

    const permitType = permitTypesStore?.getPermitTypeById(permit.typeId)
    if (!permitType) return null

    const step = permitType.workflowSteps.find(
      s => s.id === permit.currentWorkflowStepId,
    )
    if (!step) return null

    const actionLabel = getForwardActionLabel(step.type)
    if (!actionLabel) return null

    const hasMandatory = currentStepHasMandatoryFields(permit, permitTypesStore)
    const buttonText = hasMandatory
      ? Localization.translator.goToStep
      : actionLabel

    return (
      <div className="tw:flex tw:items-center mt6 notification-card-indented tw:justify-end">
        <Button variant="primary-outline" onClick={this.handleBicAction}>
          {buttonText}
        </Button>
      </div>
    )
  }

  private renderApproveActionButton(): JSX.Element {
    const { notification, projectMembersStore, userProjectsStore } = this.props
    if (notification.type !== NotificationType.NewUserCreatedViaSignUp)
      return null

    const user = projectMembersStore.getById(notification.entityId)
    if (!user) return null
    if (!canApproveUser(user, userProjectsStore)) return null

    return (
      <div className="tw:flex tw:items-center mt6 notification-card-indented tw:justify-end">
        <Button variant="primary-outline" onClick={this.handleApproveAction}>
          {Localization.translator.approve_verb}
        </Button>
      </div>
    )
  }

  // --------------- Multi-select helpers ---------------

  private renderSelectionIndicator(): JSX.Element {
    const isSelected = this.isSelected
    if (isSelected) {
      return <CheckCircleFillIcon size="sm" />
    }
    return (
      <StateUnfilledCheckboxRadioIcon
        size="sm"
        className="tw:text-muted-foreground"
      />
    )
  }

  // --------------- Actions ---------------

  private get isSelected(): boolean {
    return this.props.notificationsListStore.list.selection.selectedIds.has(
      this.props.recordId,
    )
  }

  @action.bound
  private async handleApproveAction(e: React.SyntheticEvent) {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()

    const { notification, projectMembersStore } = this.props
    const user = projectMembersStore.getById(notification.entityId)
    if (!user) return

    await projectMembersStore.approveProjectMembers([user])
  }

  @action.bound
  private handleBicAction(e: React.SyntheticEvent) {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()

    const data = this.entityData
    if (data.kind !== 'permit' || !data.permit) return

    const permit = data.permit
    const { permitTypesStore, eventsStore } = this.props
    const hasMandatory = currentStepHasMandatoryFields(permit, permitTypesStore)

    if (hasMandatory) {
      this.viewNotification()
      return
    }

    const permitType = permitTypesStore?.getPermitTypeById(permit.typeId)
    if (!permitType) return

    const step = permitType.workflowSteps.find(
      s => s.id === permit.currentWorkflowStepId,
    )
    if (!step) return

    const isLastStep = permitType.isLastStep(permit.currentWorkflowStepId)
    const targetStatus = getForwardActionTargetStatus(step.type, isLastStep)
    if (!targetStatus) return

    permit.setStatus(targetStatus)
    advanceToNextWorkflowStep(permit, permitType.stepsWithoutRecurring)
    eventsStore.dispatch(SAVE_SITE_PERMITS, [permit])
  }

  @action.bound
  private viewNotification() {
    const { notification, eventsStore } = this.props
    eventsStore.dispatch(VIEW_NOTIFICATION, notification)
  }

  @action.bound
  private setWrapperRef(ref: HTMLDivElement) {
    this.wrapperRef = ref
  }

  @action.bound
  private setContainerRef(ref: HTMLDivElement | null) {
    if (this.elementSwiper) {
      this.elementSwiper.disconnectElement()
      this.elementSwiper = null
    }

    if (ref && !this.props.isMultiSelectMode) {
      this.elementSwiper = new ElementSwiper(ref, { direction: 'right' })
      this.elementSwiper.skipOpenAnimation = true
      this.elementSwiper.on('open', this.startArchiveAnimation)
    }
  }

  @action.bound
  private startArchiveAnimation() {
    const wrapper = this.wrapperRef
    const container = this.elementSwiper?.containerElement
    if (!wrapper || !container) {
      this.doArchive()
      return
    }

    // Match the swipe area background so it's visible as the card slides away
    wrapper.style.overflow = 'hidden'
    wrapper.className = 'notification-item-swipe-area'

    // Pin wrapper height and set up transitions
    wrapper.style.height = `${wrapper.offsetHeight}px`
    wrapper.style.transition = 'height 200ms ease-out'
    container.style.transition = 'transform 200ms ease-out'
    void wrapper.offsetHeight

    // Slide card right + collapse row simultaneously
    container.style.transform = 'translateX(100%)'
    wrapper.style.height = '0px'

    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'height' || e.target !== wrapper) return
      wrapper.removeEventListener('transitionend', onEnd)
      this.doArchive()
    }
    wrapper.addEventListener('transitionend', onEnd)
  }

  private doArchive() {
    const notifications = [this.props.notification]
    this.props.eventsStore.dispatch(
      MARK_NOTIFICATIONS_AS_ARCHIVED,
      notifications,
      () => {
        if (archiveResetTimer) {
          clearTimeout(archiveResetTimer)
        }
        archiveCount++
        toast.dismiss('notification-archive')
        requestAnimationFrame(() => {
          toast.info(Localization.translator.xArchived(archiveCount), {
            timeout: ARCHIVE_TOAST_TIMEOUT,
            key: 'notification-archive',
          })
        })
        archiveResetTimer = setTimeout(() => {
          archiveCount = 0
          archiveResetTimer = null
        }, ARCHIVE_TOAST_TIMEOUT)
      },
    )
  }
}
